/**
 * Keeps the ``prism_media`` cookie alive for the signed-in user (card #277).
 *
 * ``<img>`` and ``<video>`` can't send the Clerk bearer, and until 06/10/2026
 * that is why ``/output`` and ``/api/v1/media`` served any file to anyone. The
 * backend now reads this cookie there instead. The SPA
 * (prism.lcprojects.com.br) and the API (dev-api-prism.lcprojects.com.br) are
 * the same site, so a cookie the API sets on a ``credentials: 'include'``
 * fetch rides along on every media request; in dev the Vite proxy makes it
 * same-origin outright.
 *
 * The cookie is HttpOnly, so its owner and expiry are remembered here
 * (localStorage) to know, on a reload, whether one is likely still valid —
 * and whose: after a switch of user the remembered one isn't the new user's.
 */

/** Renew this long before the cookie expires. */
export const RENEW_BEFORE_MS = 60 * 60 * 1000
/** After a failed start (API down, offline), try again this soon. */
export const RETRY_MS = 60 * 1000
/** First paint waits for the cookie at most this long. */
export const FIRST_START_WAIT_MS = 3000

const STORAGE_KEY = 'prism.mediaSession'

interface Remembered {
  owner: string
  expiresAtMs: number
}

export interface MediaSessionDeps {
  /** POST /api/v1/media/session; resolves to ``expires_at`` (epoch s) or null. */
  start: () => Promise<number | null>
  /** DELETE /api/v1/media/session. */
  end: () => Promise<void>
  now?: () => number
  setTimer?: (fn: () => void, ms: number) => unknown
  clearTimer?: (handle: unknown) => void
  storage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null
}

export interface MediaSession {
  /** Start (or keep) the cookie for ``owner``; resolves once the first start settles. */
  signIn: (owner: string) => Promise<void>
  /** Drop the cookie: before Clerk's sign-out, or on a signed-out load that remembers one. */
  signOut: () => Promise<void>
  /** A tab back from sleep renews if the timer was missed. */
  wake: () => void
  /** Whether ``owner``'s cookie is likely valid right now (enough to render media). */
  hasFreshCookie: (owner: string | null | undefined) => boolean
  /** Whether a cookie (anyone's) is remembered at all: a sign-out may have missed it. */
  hasRemembered: () => boolean
  dispose: () => void
}

export function createMediaSession(deps: MediaSessionDeps): MediaSession {
  const now = deps.now ?? (() => Date.now())
  const setTimer = deps.setTimer ?? ((fn: () => void, ms: number) => setTimeout(fn, ms))
  const clearTimer = deps.clearTimer ?? ((h: unknown) => clearTimeout(h as ReturnType<typeof setTimeout>))
  const storage = deps.storage === undefined ? safeLocalStorage() : deps.storage

  let timer: unknown = null
  let activeOwner: string | null = null
  // Bumped on sign-out, dispose and a switch of user: a start still in
  // flight then lands in an old generation and is dropped instead of
  // re-arming the timer.
  let generation = 0
  let remembered = read(storage)

  function schedule(ms: number) {
    if (timer !== null) clearTimer(timer)
    timer = setTimer(() => {
      timer = null
      void renew()
    }, ms)
  }

  function stop() {
    activeOwner = null
    generation += 1
    if (timer !== null) clearTimer(timer)
    timer = null
  }

  async function renew(): Promise<void> {
    const owner = activeOwner
    if (owner === null) return
    const mine = generation
    let expiresAt: number | null = null
    try {
      expiresAt = await deps.start()
    } catch {
      expiresAt = null
    }
    if (mine !== generation) return
    if (expiresAt) {
      remembered = { owner, expiresAtMs: expiresAt * 1000 }
      write(storage, remembered)
      schedule(Math.max(remembered.expiresAtMs - now() - RENEW_BEFORE_MS, RETRY_MS))
    } else {
      schedule(RETRY_MS)
    }
  }

  return {
    async signIn(owner) {
      if (activeOwner === owner) return
      stop() // another user's renewal, if any
      activeOwner = owner
      await renew()
    },
    async signOut() {
      stop()
      remembered = null
      remove(storage)
      try {
        await deps.end()
      } catch {
        // The cookie dies on its own at expiry; nothing else to do.
      }
    },
    wake() {
      if (activeOwner !== null && (remembered === null || now() > remembered.expiresAtMs - RENEW_BEFORE_MS)) {
        void renew()
      }
    },
    hasFreshCookie(owner) {
      return (
        !!owner &&
        remembered !== null &&
        remembered.owner === owner &&
        remembered.expiresAtMs - now() > RENEW_BEFORE_MS / 12
      )
    },
    hasRemembered() {
      return remembered !== null
    },
    dispose: stop,
  }
}

function safeLocalStorage(): Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage
  } catch {
    return null
  }
}

function read(storage: MediaSessionDeps['storage']): Remembered | null {
  try {
    const raw = storage?.getItem(STORAGE_KEY)
    if (!raw) return null
    const value = JSON.parse(raw) as Partial<Remembered>
    if (typeof value.owner !== 'string' || typeof value.expiresAtMs !== 'number') return null
    return { owner: value.owner, expiresAtMs: value.expiresAtMs }
  } catch {
    return null
  }
}

function write(storage: MediaSessionDeps['storage'], value: Remembered): void {
  try {
    storage?.setItem(STORAGE_KEY, JSON.stringify(value))
  } catch {
    // Private window or blocked storage: the cookie still works this session.
  }
}

function remove(storage: MediaSessionDeps['storage']): void {
  try {
    storage?.removeItem(STORAGE_KEY)
  } catch {
    // Same as above.
  }
}
